import { useState, useEffect } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import { useNavigate } from "react-router-dom";
import { useCreatePreliminaryUpload } from "../../lib/api/dorman";
import { FormGridHelper } from "../reports/helper";
import {
  InputRequiredStatus,
  InputFormType,
  InputType,
  ImportType,
  ExportType,
  InputFilter,
  InputRequiredStatusType,
} from "../../lib/form";
import * as FormHelper from "../../lib/form";
import { downloadFromS3, UploadPresignedFile } from "../../lib/file";
import * as FormLayout from "../../lib/form/layout";
import {
  PrimitiveGridLayout,
  PrimitiveManagedTable,
  PrimitiveDialogForm,
} from "../../lib/form/layout";
import { SuccessResult, ServerError, ValidationError } from "../../lib/responseResults";
import { useToast } from "../../components/ui/use-toast";
import { doesUserHavePackages, PossiblePackage } from "@/lib/permissions/stytch_original";
import { useGenerateInventoryMapDownloadLink, useCreateInventoryMapUpload } from "../../lib/api/inventoryMap";
import { useGenerateContractQuantityAlertJob } from "@/lib/api/reports";
import { useGetWorkJobStatus, type WorkJobStatus } from "@/lib/api/work";

const ContractQuantityAlertSubType = {
  Purchase: "purchase",
  Sale: "sale",
} as const;

const contractQuantityAlertSubTypeOptions = [
  { value: ContractQuantityAlertSubType.Purchase, label: "Purchase" },
  { value: ContractQuantityAlertSubType.Sale, label: "Sales" },
];

export const CreateJobFormSchema = {
  sub_type: {
    label: "Report Type",
    options_reference_id: "contractQuantityAlertSubTypeOptions",
    input_type: InputType.Enum,
    enum: ContractQuantityAlertSubType,
    input_type_validation: InputFormType.Enum(ContractQuantityAlertSubType),
    required_status: InputRequiredStatus.Required,
  },
  contract_quantity_file: { label: "Contract Quantity Alert Excel", fileExtensions: [".xlsx", ".xls"], input_type: InputType.File, input_type_validation: InputFormType.File, required_status: InputRequiredStatus.Required },
};

export function ContractQuantitySection() {
  const { member } = useStytchMember();
  const isAvailable = doesUserHavePackages({
    requiredPackages: [PossiblePackage.ContractQuantity],
    member,
  });
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const jobStatusRes = useGetWorkJobStatus({
    jobId,
    featureType: "contract_quantity_alert",
    jobType: "generate_automation_report",
    enabled: isAvailable,
  });

  if (!isAvailable) {
    return null;
  }

  const jobStatus = jobStatusRes.data?.data;

  return (
    <div className="container">
      <div className="flex flex-col">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl mb-4">
          Contract Quantity Upload
        </h1>
        <ContractQuantityCreateJobForm setJobId={setJobId} />
        <div className="mt-8 flex flex-col">
          {!!jobStatus && (
            <WorkJobStatusDisplay status={jobStatus} />
          )}
        </div>
      </div>
    </div>
  );
}

function WorkJobStatusDisplay({ status }: { status: WorkJobStatus }) {
  const className = "text-lg font-bold tracking-tight mb-4";

  if (status.status === "queued") {
    return (<div className={className}>Queued</div>);
  }
  if (status.status === "actively_processing") {
    return (<div className={className}>Processing</div>);
  }
  if (status.status === "halted") {
    return (<div className={className}>Failed to process</div>);
  }
  return (<div className={className}>Completed</div>);
}

const createJobState = FormHelper.generateBaseState({
  schemas: [CreateJobFormSchema],
  baseSchema: CreateJobFormSchema,
  overrideValues: { sub_type: ContractQuantityAlertSubType.Purchase },
});

function ContractQuantityCreateJobForm({ setJobId }: { setJobId: any }) {
  const inputState: any = createJobState.useInputsState((state: any) => state);
  const submissionState: any = createJobState.useSubmissionState((state: any) => state);
  const onCreateJob = useGenerateContractQuantityAlertJob();
  const { toast } = useToast();

  return FormLayout.RenderFullPrimitiveGridLayout({
    hideSkipButton: true,
    id: "CreateContractQuantityAlertUploadJob",
    activeSchema: CreateJobFormSchema,
    totalFields: inputState.totalFields,
    formValues: inputState.formValues,
    updateFormValue: inputState.updateFormValue,
    formRelationships: inputState.formRelationships,
    updateFormRelationship: inputState.updateFormRelationship,
    submissionState,
    externalReferences: { contractQuantityAlertSubTypeOptions },
    onSubmitTo: async (item: any) => {
      if (item.contract_quantity_file && ![".xlsx", ".xls"].some((extension: string) => item.contract_quantity_file?.name?.toLocaleLowerCase()?.endsWith(extension))) {
        toast({
          title: "Contract Quantity Alert File",
          description: "Must be an Excel file",
        });
        return new ValidationError("", {});
      }

      const createBody = {
        filename: item.contract_quantity_file?.name || "",
        mimeType: item.contract_quantity_file?.type || "",
        meta_data: {
          sub_type: item.sub_type || ContractQuantityAlertSubType.Purchase,
        },
      };

      const createResult = await onCreateJob(createBody);

      if (!createResult.success) {
        return createResult;
      }

      const resultData = createResult.data;
      setJobId(resultData.job_id);

      await UploadPresignedFile(resultData.link, item.contract_quantity_file);

      return createResult;
    },
    handleSubmitResponse: (result) => {
      if (result.statusCode === 400) {
        toast({
          title: "Failed to upload files",
          description: "",
        });
        return;
      } else if (!result.success) {
        toast({
          title: "Failed to upload files",
          description: "",
        });
      } else {
        toast({
          title: "Queued - Processing",
          description: "You can leave the page",
        });
      }
    }
  });
}
